import React, { useEffect, useState } from 'react';

import Loader from '../../../components/Loader';
import { apiPaths } from '../../../service/apiPaths';
import ApiService from '../../../service/ApiService';
import Leave from './Leave/Components/Leave';

interface StatusBreakdown {
  [key: string]: number | undefined;
  "in progress"?: number;
  finished?: number;
  query?: number;
  todo?: number;
  backLog?: number;
}

interface EmployeeSummary {
  employeeName: string;
  email: string;
  totalTasks: number;
  statusBreakdown: StatusBreakdown;
}

interface QueryTask {
  _id: string;
  description: string;
  priority: string;
  assignedTo: {
    _id: string;
    name: string;
    email: string;
  };
  projectId: {
    _id: string;
    name: string;
  };
  status: string;
  query: string;
  dueDate: string;
}

interface DashboardData {
  employeeSummaries: EmployeeSummary[];
  queryTasks: QueryTask[];
  totalEmployees: number;
  totalQueryTasks: number;
}

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    employeeSummaries: [],
    queryTasks: [],
    totalEmployees: 0,
    totalQueryTasks: 0,
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await ApiService({
        method: "GET",
        endpoint: apiPaths.adminDashBoard,
      });
      setDashboardData(response);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="p-6 h-[85vh] overflow-auto space-y-10">

  {/* Section 1: Current Queries */}
  {dashboardData.queryTasks.length > 0 && (
    <section>
      <h2 className="text-3xl font-semibold mb-6 text-gray-800">Current Queries</h2>
      
      <div className="overflow-x-auto bg-white rounded-xl shadow ring-1 ring-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 text-gray-700 text-sm font-semibold">
            <tr>
              <th className="px-6 py-3 text-left">Employee</th>
              <th className="px-6 py-3 text-left">Project</th>
              <th className="px-6 py-3 text-left">Task</th>
              <th className="px-6 py-3 text-left">Priority</th>
              <th className="px-6 py-3 text-left">Due Date</th>
              <th className="px-6 py-3 text-left">Query Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
            {dashboardData.queryTasks.map((task) => (
              <tr key={task._id} className="hover:bg-gray-50">
                <td className="px-6 py-3">{task.assignedTo.name}</td>
                <td className="px-6 py-3">{task.projectId.name}</td>
                <td className="px-6 py-3">{task.description}</td>
                <td className="px-6 py-3 capitalize">{task.priority}</td>
                <td className="px-6 py-3">
                  {new Date(task.dueDate).toLocaleDateString()}
                </td>
                <td className="px-6 py-3">{task.query}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )}

  {/* Section 2: Today's Task Summary */}
  <section>
    <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 space-y-2 md:space-y-0">
      <h2 className="text-3xl font-semibold text-gray-800">Today's Task Summary</h2>
      <div className="text-gray-600 text-sm space-x-4">
        <span>Total Employees: <strong>{dashboardData.totalEmployees}</strong></span>
        <span>Total Query Tasks: <strong>{dashboardData.totalQueryTasks}</strong></span>
      </div>
    </div>
<div className='flex flex-col lg:flex-row gap-6'>
    <div className="flex-1 overflow-x-auto bg-white rounded-xl shadow ring-1 ring-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 text-gray-700 text-sm font-semibold">
          <tr>
            <th className="px-6 py-3 text-left">Employee</th>
            <th className="px-6 py-3 text-center">In Progress</th>
            <th className="px-6 py-3 text-center">Finished</th>
            <th className="px-6 py-3 text-center">Query</th>
            <th className="px-6 py-3 text-center">Todo</th>
            <th className="px-6 py-3 text-center">Backlog</th>
            <th className="px-6 py-3 text-center">Total Tasks</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
          {dashboardData.employeeSummaries.map((employee, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-6 py-3 whitespace-nowrap font-medium text-gray-900">
                {employee.employeeName}
              </td>
              <td className="px-6 py-3 text-center">
                <span className="inline-flex items-center justify-center w-8 h-8 text-xs font-bold bg-yellow-100 text-yellow-800 rounded-full">
                  {employee.statusBreakdown["in progress"] || 0}
                </span>
              </td>
              <td className="px-6 py-3 text-center">
                <span className="inline-flex items-center justify-center w-8 h-8 text-xs font-bold bg-green-100 text-green-800 rounded-full">
                  {employee.statusBreakdown["finished"] || 0}
                </span>
              </td>
              <td className="px-6 py-3 text-center">
                <span className="inline-flex items-center justify-center w-8 h-8 text-xs font-bold bg-red-100 text-red-800 rounded-full">
                  {employee.statusBreakdown["query"] || 0}
                </span>
              </td>
              <td className="px-6 py-3 text-center">
                <span className="inline-flex items-center justify-center w-8 h-8 text-xs font-bold bg-blue-100 text-blue-800 rounded-full">
                  {employee.statusBreakdown["todo"] || 0}
                </span>
              </td>
              <td className="px-6 py-3 text-center">
                <span className="inline-flex items-center justify-center w-8 h-8 text-xs font-bold bg-gray-100 text-gray-800 rounded-full">
                  {employee.statusBreakdown["backLog"] || 0}
                </span>
              </td>
              <td className="px-6 py-3 text-center font-medium">
                {employee.totalTasks}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    
    {/* Leave Component */}
    <div className="flex-1">
      <Leave />
    </div>
  </div>
  </section>


</div>

  );
};

export default Dashboard;
